import { useEffect, useState, useRef } from 'react';
import { useAppStore } from '@/stores/app-store';
import { readScopedCache, writeScopedCache, purgeLegacyUnscopedCaches } from '@/lib/cache/scoped-cache';
import { useModuleStore, purgeReauthCachesAtBoot } from '@/stores/module-store';
import { useAuthStore, getEffectiveUid, loadUserDocOnce } from '@/stores/auth-store';
import { useLicenseStore } from '@/stores/license-store';
import { useStoreStore } from '@/stores/store-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useMessageStore } from '@/stores/message-store';
import { useUIStore } from '@/stores/ui-store';
import { useAppUsersStore } from '@/stores/app-users-store';
import { getAppCodeFromURL, getViewPathFromURL } from '@/lib/utils/url';
import { setStorageItem, getStorageItem } from '@/lib/utils/storage';
import { runWithConcurrency } from '@/lib/utils/concurrency';
import { extractWaUsernameFields } from '@/lib/utils/crm-phone-fields';
import { EVENT_APPS, CALL_APPS, INBOX_SUPPORTED_TPAS, NON_MESSAGING_TPAS, CHAT_ONLY_TPAS, resolveNonMessagingView, type ViewType } from '@/config/constants';
import { onAuthStateChanged } from 'firebase/auth';
import { auth as firebaseAuth } from '@/config/firebase';
import { useTrialStore } from '@/stores/trial-store';
import { idbCache } from '@/lib/cache/idb-cache';
import type { CrmPhoneField } from '@/stores/ui-store';
import type { CRMRecord, CRMField } from '@/types/saas';
import { triggerAlertsCleanup } from '@/lib/firebase/rtdb-cleanup';
import { stampLastAccessTime } from '@/lib/firebase/last-access';

// Track page load time for performance logging
const PAGE_LOAD_TIME = performance.now();

// ── Early Pipedrive SDK init (before auth) ──────────────────────────────────
// Pipedrive requires AppExtensionsSDK.initialize() to display the custom UI.
// The SDK reads the `id` query param from the URL, so this must run before
// use-app-init strips query params via replaceState. Fire immediately at
// module level (outside React) — the Pipedrive SaaS module picks up the
// resolved SDK instance via window.__pipedriveSDKPromise.
(() => {
  try {
    if (window.self === window.top) return; // not embedded
    const params = new URLSearchParams(window.location.search);
    if (params.get('target') === 'appcursor') return; // not Pipedrive's iframe
    const appCode = params.get('appCode') || params.get('appcode') || '';
    // Only init for Pipedrive extensions (appCode contains "pipedrive")
    if (!appCode.toLowerCase().includes('pipedrive')) return;
    const id = params.get('id');
    if (!id) return;
    const openMode = params.get('openMode');
    import('@pipedrive/app-extensions-sdk').then(({ default: AppExtensionsSDK, Command }) => {
      const sdkPromise = new AppExtensionsSDK({ identifier: id }).initialize()
        .then(async (sdk) => {
          // Resize immediately after init — matches old code pattern
          if (openMode === 'modal') {
            await sdk.execute(Command.RESIZE, { height: 600, width: 800 });
          } else {
            await sdk.execute(Command.RESIZE, { height: 100, width: 800 });
          }
          return sdk;
        });
      (window as Record<string, unknown>).__pipedriveSDKPromise = sdkPromise;
      console.log('[Init] Pipedrive SDK init fired early (before auth)');
    });
  } catch { /* not in iframe or no SDK */ }
})();

function logWithTimestamp(message: string) {
  const elapsed = performance.now() - PAGE_LOAD_TIME;
  console.log(`[Init +${elapsed.toFixed(0)}ms]`, message);
}

/** CRM record context cached in IDB.
 *  Stores id, name, phone field/value pairs, AND all non-object record properties
 *  so that the merge field dropdown (#) shows all available fields immediately on load.
 *  Matches original app's FETCHED_RECORD_DETAILS which stores the full record. */
interface CachedCrmContext {
  id: string;
  displayName?: string;
  email?: string;
  phones: Array<{ fieldName: string; value: string; label?: string }>;
  /** All non-object string/number properties from the CRM record — for merge field placeholders */
  properties?: Record<string, string>;
}

/** IDB cache key for CRM record context — scoped to extension + entity */
function getCrmContextCacheKey(ext: string, module: string, entityId: string): string {
  return `crm_context_${ext}_${module}_${entityId}`;
}

/** TTL for CRM record context cache — 1 week */
const CRM_CONTEXT_CACHE_TTL = 7 * 24 * 60 * 60 * 1000;

export type InitStep =
  | 'parsing'
  | 'modules'
  | 'auth'
  | 'org'
  | 'finalizing'
  | 'done';

const STEP_LABELS: Record<InitStep, string> = {
  parsing: 'Loading configuration...',
  modules: 'Loading modules...',
  auth: 'Checking authentication...',
  org: 'Fetching organization...',
  finalizing: 'Preparing workspace...',
  done: 'Ready',
};

/**
 * Hook for app initialization flow — optimized for speed.
 *
 * Critical path (blocks UI): appCode parse → modules load → SaaS auth → org fetch → READY
 * Background (non-blocking): settings load + credits fetch run after ready
 */
export function useAppInit() {
  const [ready, setReady] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<InitStep>('parsing');

  // Ref to track if initialization has already run (prevents duplicate calls in StrictMode)
  const initStartedRef = useRef(false);
  const initCompletedRef = useRef(false);

  const user = useAuthStore((s) => s.user);
  const { parseAndSetAppCode, appCodeTPA, appCodeSAAS, extensionName } = useAppStore();
  const { loadModules, saasLoading, tpaLoading, saasError, tpaError } = useModuleStore();

  useEffect(() => {
    if (!user) return;

    // Prevent duplicate initialization (handles StrictMode double-invoke)
    if (initStartedRef.current) {
      console.log('[AppInit] Skipping duplicate initialization (already started)');
      return;
    }
    initStartedRef.current = true;
    console.log('[AppInit] Starting initialization...');

    const init = async () => {
      try {
        // 0. If a TPA or SaaS re-auth was pending when this page loaded
        //    popup reloads the opener directly; or the purge was interrupted
        //    mid-flight), finish the account-cache purge BEFORE any store
        //    reads org_data/templates. No reload — this load is fresh.
        await purgeReauthCachesAtBoot();

        // 0b. Restore license cache from IDB (async but fast — no network)
        useLicenseStore.getState().restoreFromCache().catch(() => {});

        // 1. Get appCode from URL or localStorage
        logWithTimestamp('START: Getting appCode');
        setCurrentStep('parsing');
        const viewPath = getViewPathFromURL();
        const isKanbanRoute = viewPath === 'inbox/kanban';
        const isInboxRoute = viewPath === 'inbox' || isKanbanRoute;
        const isComposeRoute = viewPath === 'compose';
        const isSettingsRoute = viewPath === 'settings';
        const isCallsRoute = viewPath === 'calls';
        const isCallHistoryRoute = viewPath === 'call-history';
        const isVoicemailRoute = viewPath === 'voicemail';
        const isRecordingsRoute = viewPath === 'recordings';
        const isCallQueuesRoute = viewPath === 'call-queues';
        const isCallPathRoute = isCallsRoute || isCallHistoryRoute || isVoicemailRoute || isRecordingsRoute || isCallQueuesRoute;
        const isScheduleRoute = viewPath === 'schedule';
        const isHistoryRoute = viewPath === 'history';
        const isLinksRoute = viewPath === 'links';
        const isEventPathRoute = isScheduleRoute || isHistoryRoute || isLinksRoute;

        // Save query params BEFORE stripping (needed for force-ui-view etc.)
        const queryParams = new URLSearchParams(window.location.search);

        // Clean path-only URLs — strip only the query params that are already
        // encoded in the path (`appCode`, `force-ui-view`). Other params
        // (`openChatId`, `actionType`, `dc`, `openMode`, CRM SDK params, …)
        // carry meaningful state and must survive init.
        const isAiActionsRoute = window.location.pathname === '/ai-actions';
        if (viewPath && window.location.search) {
          const url = new URL(window.location.href);
          const PATH_REDUNDANT = ['appCode', 'appcode', 'force-ui-view'];
          let changed = false;
          for (const k of PATH_REDUNDANT) {
            if (url.searchParams.has(k)) {
              url.searchParams.delete(k);
              changed = true;
            }
          }
          if (changed) {
            window.history.replaceState({}, '', url.pathname + (url.search ? url.search : '') + url.hash);
            logWithTimestamp('Stripped path-redundant query params (kept meaningful ones)');
          }
        } else if (isAiActionsRoute && window.location.search) {
          // /ai-actions is a single-view route, so most `force-ui-view` values
          // (MESSAGE_FORM, CONVERSATIONS, …) are meaningless here. EXCEPT
          // `SETTINGS` (and `SETTINGS-<cardId>`) — they open the settings
          // overlay, and deep links / open-in-new-tab need them to survive
          // init so the new tab lands on the settings card the user clicked
          // through to. Strip everything else.
          const url = new URL(window.location.href);
          const raw = url.searchParams.get('force-ui-view') || '';
          const upper = raw.toUpperCase();
          const isSettingsValue = upper === 'SETTINGS' || upper.startsWith('SETTINGS-');
          if (raw && !isSettingsValue) {
            url.searchParams.delete('force-ui-view');
            window.history.replaceState({}, '', url.pathname + (url.search ? url.search : '') + url.hash);
            logWithTimestamp('Stripped non-SETTINGS force-ui-view from /ai-actions URL');
          }
        }

        let appCode = getAppCodeFromURL() || queryParams.get('appCode') || queryParams.get('appcode');
        if (!appCode) {
          appCode = getStorageItem('last_app_code', null);
          // Add to URL so refreshes and shared links retain the appCode
          // (skip for /inbox/ routes — appCode is in the path)
          if (appCode && !isInboxRoute && !isComposeRoute && !isSettingsRoute && !isCallPathRoute) {
            const url = new URL(window.location.href);
            url.searchParams.set('appCode', appCode);
            window.history.replaceState({}, '', url.toString());
            logWithTimestamp(`Restored appCode from localStorage: ${appCode}`);
          }
        }

        if (!appCode) {
          setInitError('No appCode provided');
          setReady(true);
          return;
        }

        purgeLegacyUnscopedCaches();

        // Org id as confirmed by a real getCurrentOrg() call this session.
        // Stays null when SaaS auth is missing OR the org API failed, which is
        // what the Firestore org_id sync keys off — appConfig.orgId can't be
        // used there because it may still hold the cache-seeded value below.
        let liveOrgId: string | null = null;

        // 2. Parse appCode
        logWithTimestamp('START: Parsing appCode');
        const success = parseAndSetAppCode(appCode);
        if (!success) {
          setInitError(`Invalid appCode: ${appCode}`);
          setReady(true);
          return;
        }
        logWithTimestamp('DONE: Parsing appCode');

        // Scope for the ua_cache_* app caches. MUST come from the store, not
        // the raw URL `appCode`: parseAndSetAppCode lowercases both halves and
        // resolves the TPA alias (`kudosityforpipedrive` → `burstsmsforpipedrive`,
        // `TwilioForZohoCRM` → `twilioforzohocrm`). Every other caller lets
        // resolveScope() default to extensionName, so keying writes off the raw
        // string here would put reads and writes on different keys — a permanent
        // cache miss for any URL with different casing or an aliased TPA.
        // Captured once because an inner block further down shadows `appCode`.
        const cacheScope = { uid: user.uid, appCode: useAppStore.getState().extensionName };

        // Save for next time — but not when we're an embedded panel
        // (`?iframeId=`, e.g. the in-conversation LookUp iframe or the
        // extension-injected one). That app was never the user's choice, and
        // localStorage is shared with the host on the same origin, so writing
        // it here would send the next appCode-less load into the panel app
        // instead of the inbox they actually opened.
        if (!queryParams.get('iframeId')) {
          setStorageItem('last_app_code', appCode);
        }

        // Eager-fire trial-paywall data fetches as soon as `extensionName` is
        // resolved. Without this, the loads only kick off when components
        // call `useTrial` (after mount), and the L2 background sync runs at
        // T+10s — long enough to make the banners flicker through wrong
        // states. Each fetch dedupes via its own loaded flag.
        const ext = useAppStore.getState().extensionName;
        const uid = useAuthStore.getState().user?.uid;
        const isSharedAccess = !!useAuthStore.getState().selectedAdminUserId;
        if (ext) {
          useStoreStore.getState().loadProducts(ext).catch(() => {});
        }
        if (uid && !isSharedAccess) {
          // Owners need owned subs (and license owners) for the activation
          // and cancelled-banner detection paths.
          useStoreStore.getState().loadSubscriptions(uid).catch(() => {});
          useStoreStore.getState().loadLicenseOwners(uid).catch(() => {});
        }
        // Re-fetch licensed subs now that `extensionName` is known so the
        // license-store filters by current app (the cache-restore fetch ran
        // earlier without an extensionName and returned cross-app data).
        useLicenseStore.getState().fetchLicenseSubscriptions().catch(() => {});

        // 2b. Set initial view immediately — event apps show EVENTS, not Compose
        const resolvedTPAEarly = useAppStore.getState().appCodeTPA;
        const isEventApp = resolvedTPAEarly && (EVENT_APPS as readonly string[]).includes(resolvedTPAEarly);

        if (isEventApp) {
          // Settings route/param takes priority even for event apps
          const rawForceViewEarly = queryParams.get('force-ui-view')?.toUpperCase() || '';
          if (isSettingsRoute || rawForceViewEarly === 'SETTINGS' || rawForceViewEarly.startsWith('SETTINGS-')) {
            // Settings is an overlay — `setView('SETTINGS')` only flips
            // `settingsOpen`, leaving `activeView` at its default MESSAGE_FORM.
            // Resolve EVENTS as the underlying view first so closing Settings
            // reveals the events list, not the compose view (event apps have
            // no compose view). Mirrors the non-event settings-route branch.
            useUIStore.getState().setView('EVENTS');
            useUIStore.getState().setView('SETTINGS');
            // Auto-open settings card from SETTINGS-{cardId} param
            const originalForceView = queryParams.get('force-ui-view') || '';
            const settingsCard = /^SETTINGS-(.+)$/i.test(originalForceView)
              ? originalForceView.replace(/^SETTINGS-/i, '')
              : null;
            if (settingsCard) {
              useUIStore.getState().setActiveSettingsTab(settingsCard);
            }
          } else {
            useUIStore.getState().setView('EVENTS');
            // Set initial event tab from URL path
            if (isHistoryRoute) {
              import('@/stores/events-store').then(({ useEventsStore }) => useEventsStore.getState().setActiveTab('history'));
            } else if (isLinksRoute) {
              import('@/stores/events-store').then(({ useEventsStore }) => useEventsStore.getState().setActiveTab('links'));
            }
            // /schedule/ maps to 'new' which is the default — no action needed
          }
          useUIStore.getState().setViewResolved(true);
          // Event apps can also be opened in CRM record context — detect entityId
          // Also check itemId (Monday.com) and selectedIds (multi-record selection)
          const entityId = queryParams.get('entityId') || queryParams.get('Entities')
            || queryParams.get('itemId') || queryParams.get('selectedIds');
          if (entityId) {
            useUIStore.getState().setCrmRecordContext(true);
          }
        }

        // 3. Build module context (seed from localStorage cache for faster startup)
        logWithTimestamp('START: Building module context');
        const context = {
          user,
          config: {
            // Scoped by uid+appCode: a different account (or a different app)
            // misses the cache instead of seeding this context with the
            // previous account's org id — which used to leak through until a
            // manual reload and showed the wrong trial period.
            appUniqueId: readScopedCache('ua_cache_appUniqueId', cacheScope) || '',
            orgId: readScopedCache('ua_cache_appOrgId', cacheScope) || '',
            extensionName: useAppStore.getState().extensionName || appCode,
            appCodeTPA: useAppStore.getState().appCodeTPA || '',
            appCodeSAAS: useAppStore.getState().appCodeSAAS || '',
          },
          queryParams,
        };

        // Set appConfig early from cache so settings can load even before SaaS auth completes.
        // loadModules() only awaits TPA (SaaS loads in background), so checkSaaSAuth() may
        // return false → setConfig() in step 6 is skipped → appConfig stays null → settings fail.
        if (context.config.orgId) {
          useAppStore.getState().setConfig(context.config);
          logWithTimestamp('DONE: Set appConfig from cache (orgId available)');
        }

        // Determine view IMMEDIATELY based on URL params (before any async operations)
        // This shows the correct view's skeleton instantly
        // Skip view determination for event apps — they already have their view set

        logWithTimestamp('START: Determining view');
        const rawForceView = queryParams.get('force-ui-view')?.toUpperCase() || '';
        const forceView = (rawForceView.startsWith('SETTINGS-') ? 'SETTINGS' : rawForceView) as ViewType | undefined;
        const entityId = queryParams.get('entityId') || queryParams.get('Entities')
          || queryParams.get('itemId') || queryParams.get('selectedIds');

        const tpaCode = useAppStore.getState().appCodeTPA || '';
        // Whether the full inbox VIEW may be resolved as a default. Distinct
        // from INBOX_SUPPORTED_TPAS on purpose: a chat-only app still uses that
        // set to earn the CHAT view in single-record context, it just must never
        // land on CONVERSATIONS. Every branch below that defaults to the inbox
        // reads this, so deleting the app from CHAT_ONLY_TPAS restores them all.
        const inboxViewAllowed = INBOX_SUPPORTED_TPAS.has(tpaCode) && !CHAT_ONLY_TPAS.has(tpaCode);

        if (!isEventApp) {
          // AI Actions has its own dedicated page (/ai-actions) with a single
          // view — skip view determination entirely so we don't write
          // `force-ui-view=MESSAGE_FORM` to the URL.
          if (tpaCode === 'aiactions') {
            // Honour `force-ui-view=SETTINGS` (and `SETTINGS-<cardId>`) so
            // deep links / open-in-new-tab on the AI Actions settings panels
            // open the overlay on load. Auto-open the requested settings
            // card when the param carries an id suffix.
            if (forceView === 'SETTINGS') {
              useUIStore.getState().setView('SETTINGS');
              const originalForceView = queryParams.get('force-ui-view') || '';
              const settingsCard = /^SETTINGS-(.+)$/i.test(originalForceView)
                ? originalForceView.replace(/^SETTINGS-/i, '')
                : null;
              if (settingsCard) {
                useUIStore.getState().setActiveSettingsTab(settingsCard);
              }
              logWithTimestamp('View forced via force-ui-view for aiactions: SETTINGS');
            }
            useUIStore.getState().setViewResolved(true);
            logWithTimestamp('Skipping view determination for aiactions TPA');
          } else if (tpaCode === 'lookup') {
            useUIStore.getState().setView('LOOKUP');
            if (isSettingsRoute || forceView === 'SETTINGS') {
              useUIStore.getState().setView('SETTINGS');
              logWithTimestamp('View forced for lookup app: LOOKUP + SETTINGS overlay');
            } else {
              logWithTimestamp('View forced for lookup app: LOOKUP');
            }
          } else if (isCallHistoryRoute) {
            // Chrome extension context menu: /twiliofor{saas}/call-history?callNumber=...&target=cursor-contextMenu
            // Opens CRM call manager with call history pre-filtered by the number
            const callNumber = queryParams.get('callNumber') || '';
            useUIStore.getState().setView('CONVERSATIONS');
            useUIStore.getState().setCrmRecordContext(true);
            // Store callNumber for call-history auto-search and open CRM call manager
            import('@/stores/call-store').then(({ useCallStore }) => {
              if (callNumber) {
                useCallStore.getState().setContextMenuCallNumber(callNumber);
                useCallStore.getState().setDialerNumber(callNumber);
              }
              // Ensure CRM call manager is open
              if (!useCallStore.getState().crmCallManagerOpen) {
                useCallStore.getState().toggleCrmCallManager();
              }
            });
            logWithTimestamp(`View forced via /call-history/ route: CONVERSATIONS + CRM call manager (callNumber: ${callNumber})`);
          } else if (isCallsRoute) {
            useUIStore.getState().setView('CONVERSATIONS');
            logWithTimestamp('View forced via /calls/ route: CONVERSATIONS');
          } else if (isVoicemailRoute || isRecordingsRoute) {
            useUIStore.getState().setView('CONVERSATIONS');
            setTimeout(() => {
              const el = document.querySelector('[data-tab="recordings"]') as HTMLElement;
              el?.click();
            }, 100);
            logWithTimestamp('View forced via /recordings/ route');
          } else if (isCallQueuesRoute) {
            useUIStore.getState().setView('CONVERSATIONS');
            setTimeout(() => {
              const el = document.querySelector('[data-tab="queues"]') as HTMLElement;
              el?.click();
            }, 100);
            logWithTimestamp('View forced via /call-queues/ route');
          } else if (isInboxRoute && CHAT_ONLY_TPAS.has(tpaCode)) {
            // The inbox is held back for chat-only apps; a typed /inbox URL
            // (or a stale bookmark) lands on compose instead of rendering a
            // view every menu hides. Covers /inbox/kanban too.
            //
            // A record in the URL still wins, and this branch has to honour it
            // itself: not-found-page rewrites a bare `/{appCode}?entityId=…`
            // to `/{appCode}/inbox?entityId=…` before init runs, so every
            // CRM-record open arrives here and never reaches the entityId
            // branch below. Sending those to compose dropped the chat view for
            // the one case it matters most.
            const inboxRouteEntityIds = entityId ? entityId.split(',').filter(Boolean) : [];
            if (inboxRouteEntityIds.length > 0) {
              useUIStore.getState().setCrmRecordContext(true);
              useModuleStore.getState().setCrmRecordsLoading(true);
            }
            // One record → the chat for that record. Several → bulk compose,
            // the same rule the entityId branch below applies.
            const inboxRouteView = inboxRouteEntityIds.length === 1 ? 'CHAT' : 'MESSAGE_FORM';
            if (inboxRouteView === 'CHAT') {
              useUIStore.getState().setView('CHAT');
            } else {
              // Set the state directly rather than through setView, which would
              // rewrite the path to /compose. That rewrite made the open
              // non-idempotent: a record supplied later by the SDK still reached
              // CHAT on this load, but the URL now said /compose, so the next
              // load of it — a deploy-hash reload, an admin switch, the CRM
              // re-navigating the frame — resolved to compose and lost the chat.
              // Leaving the path alone keeps every load resolving the same way.
              useUIStore.setState({ activeView: 'MESSAGE_FORM', settingsOpen: false });
            }
            logWithTimestamp(`Inbox route on chat-only TPA resolved to ${inboxRouteView} (records=${inboxRouteEntityIds.length})`);
          } else if (isKanbanRoute) {
            useUIStore.getState().setView('KANBAN');
            logWithTimestamp('View forced via /inbox/kanban route: KANBAN');
          } else if (isInboxRoute) {
            useUIStore.getState().setView('CONVERSATIONS');
            logWithTimestamp('View forced via /inbox/ route: CONVERSATIONS');
          } else if (isComposeRoute) {
            useUIStore.getState().setView('MESSAGE_FORM');
            logWithTimestamp('View forced via /compose route: MESSAGE_FORM');
          } else if (isSettingsRoute) {
            // Settings is an overlay — `setView('SETTINGS')` only flips
            // `settingsOpen`, it does not touch `activeView`. On a direct load
            // of /<app>/settings the default `activeView` is `MESSAGE_FORM`,
            // so closing Settings would reveal the compose view even for
            // inbox/call apps. Resolve a sensible underlying view first, then
            // open Settings on top.
            //
            // Mirror the same CRM-context rules used elsewhere:
            //   - Multiple records  → MESSAGE_FORM (bulk compose only)
            //   - Single record     → CHAT (if inbox TPA) else MESSAGE_FORM
            //   - No record
            //       - Call app      → CONVERSATIONS
            //       - Non-messaging → tpa-specific view
            //       - Inbox TPA     → CONVERSATIONS
            //       - otherwise     → MESSAGE_FORM
            const entityIds = entityId ? entityId.split(',').filter(Boolean) : [];
            const tpaSupportsInbox = INBOX_SUPPORTED_TPAS.has(tpaCode);
            if (entityId) {
              useUIStore.getState().setCrmRecordContext(true);
              useModuleStore.getState().setCrmRecordsLoading(true);
            }
            if (entityIds.length > 1) {
              useUIStore.getState().setView('MESSAGE_FORM');
            } else if (entityIds.length === 1) {
              useUIStore.getState().setView(tpaSupportsInbox ? 'CHAT' : 'MESSAGE_FORM');
            } else if ((CALL_APPS as readonly string[]).includes(tpaCode)) {
              useUIStore.getState().setView('CONVERSATIONS');
            } else if (NON_MESSAGING_TPAS.has(tpaCode)) {
              useUIStore.getState().setView(resolveNonMessagingView(tpaCode, rawForceView));
            } else if (inboxViewAllowed) {
              useUIStore.getState().setView('CONVERSATIONS');
            } else {
              useUIStore.getState().setView('MESSAGE_FORM');
            }
            useUIStore.getState().setView('SETTINGS');
            logWithTimestamp(`View forced via /settings route: SETTINGS overlay on resolved underlying view (entityIds=${entityIds.length})`);
          } else if ((CALL_APPS as readonly string[]).includes(tpaCode)) {
            // Call-centric apps — default to CONVERSATIONS (call center)
            // app-page.tsx auto-opens CRM call manager unless on /inbox or /calls path
            if (forceView === 'SETTINGS') {
              useUIStore.getState().setView('SETTINGS');
            } else if (forceView === 'PHONE_TREE') {
              useUIStore.getState().setView('PHONE_TREE');
            } else {
              useUIStore.getState().setView('CONVERSATIONS');
            }
            if (entityId) {
              useUIStore.getState().setCrmRecordContext(true);
            }
            logWithTimestamp(`View set for call app: ${tpaCode}`);
          } else if (NON_MESSAGING_TPAS.has(tpaCode)) {
            // Non-messaging TPAs: only allow SETTINGS from force-ui-view, block messaging views
            if (forceView === 'SETTINGS') {
              // Settings is an overlay: put the app's own view underneath first, so
              // closing it lands there and never on the messaging form.
              useUIStore.getState().setView(resolveNonMessagingView(tpaCode));
              useUIStore.getState().setView('SETTINGS');
              // Deep links land on a card: force-ui-view=SETTINGS-<cardId>
              const settingsCard = /^SETTINGS-(.+)$/i.test(rawForceView)
                ? (queryParams.get('force-ui-view') || '').replace(/^SETTINGS-/i, '')
                : null;
              if (settingsCard) useUIStore.getState().setActiveSettingsTab(settingsCard);
              logWithTimestamp('View forced via URL param for non-messaging TPA: SETTINGS');
            } else {
              useUIStore.getState().setView(resolveNonMessagingView(tpaCode, rawForceView));
              logWithTimestamp(`View set for non-messaging TPA: ${tpaCode}`);
            }
            if (entityId) {
              useUIStore.getState().setCrmRecordContext(true);
            }
          } else if (forceView && ['MESSAGE_FORM', 'CONVERSATIONS', 'SETTINGS', 'CHAT'].includes(forceView)) {
            // force-ui-view param overrides all other view logic
            // CRM context view rules:
            // - Multiple records → MESSAGE_FORM (bulk compose only)
            // - Single record → CHAT or CONVERSATIONS only
            // - SETTINGS always respected
            const entityIds = entityId ? entityId.split(',').filter(Boolean) : [];
            const tpaSupportsInbox = INBOX_SUPPORTED_TPAS.has(tpaCode);
            const resolvedView = forceView === 'SETTINGS' ? 'SETTINGS'
              : !tpaSupportsInbox ? 'MESSAGE_FORM'
              : entityIds.length > 1 ? 'MESSAGE_FORM'
              // A chat-only app collapses the CONVERSATIONS request to CHAT here
              // rather than opening the inbox on top of a single record.
              : entityIds.length === 1 ? (forceView === 'CONVERSATIONS' && inboxViewAllowed ? 'CONVERSATIONS' : 'CHAT')
              // With no record the bare forceView would be the inbox, which a
              // chat-only app must not open. setView writes force-ui-view back
              // into the URL, so letting it through here makes it stick.
              : (forceView === 'CONVERSATIONS' && !inboxViewAllowed) ? 'MESSAGE_FORM'
              : forceView;
            // Set CRM context BEFORE view — prevents flash of ConversationsView
            // when CHAT view is set but crmRecordContext isn't true yet
            if (entityId) {
              useUIStore.getState().setCrmRecordContext(true);
              // Pre-set crmRecordsLoading so conversations-view doesn't race ahead
              // and load all conversations before step 7 has a chance to populate
              // crmPhoneFields. Without this, the useEffect sees crmRecordsLoading=false
              // + crmPhoneFields=[] and falls through to normal (unfiltered) loading.
              useModuleStore.getState().setCrmRecordsLoading(true);
            }
            useUIStore.getState().setView(resolvedView);
            // Auto-open settings card from URL params (e.g. SETTINGS-subscription or actionType=notificationRules)
            if (forceView === 'SETTINGS') {
              // Use original (non-uppercased) value to preserve card ID casing
              const originalForceView = queryParams.get('force-ui-view') || '';
              const settingsCard = /^SETTINGS-(.+)$/i.test(originalForceView)
                ? originalForceView.replace(/^SETTINGS-/i, '')
                : null;
              const actionType = queryParams.get('actionType');
              if (settingsCard) {
                useUIStore.getState().setActiveSettingsTab(settingsCard);
              } else if (actionType === 'notificationRules') {
                useUIStore.getState().setActiveSettingsTab('woo-notification-rules');
              }
            }
            logWithTimestamp(`View forced via URL param: ${forceView}`);
          } else if (entityId) {
            // CRM record context detected
            useUIStore.getState().setCrmRecordContext(true);
            useModuleStore.getState().setCrmRecordsLoading(true);
            // Check if multiple entity IDs (comma-separated or array)
            const entityIds = entityId.split(',').filter(Boolean);
            if (entityIds.length === 1 && INBOX_SUPPORTED_TPAS.has(tpaCode)) {
              // Only show CHAT view for TPAs that support inbox/conversations.
              // Compose-only TPAs (fast2sms, etc.) stay on MESSAGE_FORM.
              useUIStore.getState().setView('CHAT');
            } else {
              useUIStore.getState().setView('MESSAGE_FORM');
            }
          } else {
            // No CRM context from URL — default to inbox only if TPA supports it
            const saasCodeForView = useAppStore.getState().appCodeSAAS || '';

            if (saasCodeForView === 'googlesheets' || saasCodeForView === 'spreadsheet') {
              // Google Sheets / Spreadsheet always defaults to compose view — data arrives
              // via postMessage or CSV upload, needs the recipient panel, not conversations
              useUIStore.getState().setView('MESSAGE_FORM');
            } else if (inboxViewAllowed) {
              useUIStore.getState().setView('CONVERSATIONS');
            } else {
              useUIStore.getState().setView('MESSAGE_FORM');
            }
            // When a SaaS module exists, the SDK may provide widget context later
            // (step 7). Pre-set crmRecordsLoading so ConversationsView doesn't
            // race ahead and load all conversations before SDK context resolves.
            const saasCode = useAppStore.getState().appCodeSAAS;
            if (saasCode) {
              useModuleStore.getState().setCrmRecordsLoading(true);
            }
          }
          useUIStore.getState().setViewResolved(true);
          logWithTimestamp('DONE: View determined and resolved');
        }

        // 3b. Resolve selectedAdminUserId — immediate localStorage, then verify from token claims.
        //     localStorage is synchronous and available instantly; token claims require waiting
        //     for Firebase Auth to resolve from IndexedDB (~500-2000ms).
        if (!useAuthStore.getState().selectedAdminUserId) {
          const ext = useAppStore.getState().extensionName;
          const uid = user?.uid;
          if (ext && uid) {
            const tpaBase = ext.includes('for') ? ext.split('for')[0] : ext;
            const appCode = `${tpaBase}forpage`;

            // Immediate: read from localStorage (sync, no wait)
            try {
              const cachedAdminId = localStorage.getItem(
                `UA_LIC_UTILITY___CURRENT_SELECTED_ORG_ADMIN_USER_ID_${ext}_${uid}`
              );
              if (cachedAdminId) {
                useAuthStore.getState().setSelectedAdminSilent(cachedAdminId, null);
                logWithTimestamp(`selectedAdminUserId set from localStorage: ${cachedAdminId}`);
              }
            } catch { /* localStorage unavailable */ }

            // Async: verify/update from token claims (source of truth)
            logWithTimestamp(`Resolving selectedAdminUserId from token claims (appCode: ${appCode})...`);
            const fbUserPromise = firebaseAuth.currentUser
              ? Promise.resolve(firebaseAuth.currentUser)
              : new Promise<import('firebase/auth').User | null>((resolve) => {
                  const unsub = onAuthStateChanged(firebaseAuth, (u) => { unsub(); resolve(u); });
                });
            fbUserPromise.then((fbUser) => {
              if (!fbUser) {
                logWithTimestamp('No Firebase user available for token claims');
                return;
              }
              logWithTimestamp('Firebase user resolved, reading token claims...');
              return fbUser.getIdTokenResult();
            }).then((tokenResult) => {
              if (!tokenResult) return;
              console.log('[Init] Full token claims:', JSON.stringify(tokenResult.claims, null, 2));
              const apps = tokenResult.claims?.apps as Record<string, { adminId?: string }> | undefined;
              const adminId = apps?.[ext]?.adminId || apps?.[appCode]?.adminId;
              logWithTimestamp(`Token claims resolved — adminId: ${adminId || 'none'}`);
              if (adminId) {
                const current = useAuthStore.getState().selectedAdminUserId;
                if (current !== adminId) {
                  useAuthStore.getState().setSelectedAdminSilent(adminId, null);
                  logWithTimestamp(`selectedAdminUserId updated from token claims: ${current} → ${adminId}`);
                }
              }
            }).catch((err) => {
              console.warn('[Init] Failed to read adminId from token claims:', err);
            }).finally(() => {
              useAuthStore.setState({ selectedAdminResolved: true });
            });
          } else {
            useAuthStore.setState({ selectedAdminResolved: true });
          }
        } else {
          useAuthStore.setState({ selectedAdminResolved: true });
        }

        // 4. Load modules (SaaS + TPA load in parallel inside loadModules)
        logWithTimestamp('START: Loading modules');
        setCurrentStep('modules');
        const saas = useAppStore.getState().appCodeSAAS;
        const tpa = useAppStore.getState().appCodeTPA;
        if (saas && tpa) {
          await loadModules(saas, tpa, context);
        }
        logWithTimestamp('DONE: Modules loaded (TPA ready)');

        // Record that this member used the app. The legacy app stamps this on
        // startup; the rewrite never did, which is why team members show as
        // inactive in the support dashboard no matter how much they use it.
        stampLastAccessTime(useAuthStore.getState().user?.uid, useAppStore.getState().extensionName);

        // 4b. ALWAYS wait for SaaS module to finish loading before proceeding.
        //     loadModules() only awaits TPA; SaaS loads in background.
        //     All users (including shared users) have their own SaaS account,
        //     so we need saasModule ready for checkSaaSAuth + getCurrentOrg.
        if (!useModuleStore.getState().saasModule && useModuleStore.getState().saasLoading) {
          logWithTimestamp('Waiting for SaaS module to finish loading...');
          while (!useModuleStore.getState().saasModule && useModuleStore.getState().saasLoading) {
            await new Promise(resolve => setTimeout(resolve, 50));
          }
          logWithTimestamp('SaaS module loading complete');
        }

        // 4b+. Start suggested login email fetch (non-blocking; awaited only if SaaS not authed)
        const suggestedEmailPromise: Promise<string | null> = (() => {
          const sm = useModuleStore.getState().saasModule;
          return sm?.getSuggestedLoginEmail
            ? sm.getSuggestedLoginEmail().catch(() => null)
            : Promise.resolve(null);
        })();

        // 4c. Resolve widget context view from SDK (auth-independent).
        //     For query-param flow, entityId is detected in step 3 and the view
        //     is already set to CHAT. For SDK flow, the widget context is set
        //     during onInit() (step 4) but step 3 had no entityId in the URL.
        //     Switch the view here so the user sees CHAT immediately, before
        //     waiting for auth. CRM record fetching in step 7 still needs auth.
        {
          const saasModule = useModuleStore.getState().saasModule;
          if (saasModule) {
            // Apply the SDK widget-context view switch. Reads getWidgetContext()
            // (synchronous) and flips the view to CHAT/MESSAGE_FORM.
            const applyWidgetContextView = () => {
              const wc = saasModule.getWidgetContext?.();
              if (wc?.module && wc?.entityId && !useUIStore.getState().crmRecordContext) {
                useUIStore.getState().setCrmRecordContext(true);
                // Mark records as loading too — without this, ConversationsView's
                // effect sees `crmRecordContext=true` + `crmRecordsLoading=false`
                // + empty `crmPhoneFields` and immediately falls through to an
                // unfiltered load before the blocking fetch can populate phones.
                useModuleStore.getState().setCrmRecordsLoading(true);
                const currentTpaCode = useAppStore.getState().appCodeTPA || '';
                const currentView = useUIStore.getState().activeView;
                const tpaSupportsInbox = INBOX_SUPPORTED_TPAS.has(currentTpaCode);
                // A chat-only app never holds CONVERSATIONS here — the hold-back
                // resolved to MESSAGE_FORM long before the SDK answered. Inside
                // the real CRM widget the record arrives only from the SDK, so
                // without this the switch below never fires and the record opens
                // on compose instead of its chat.
                //
                // Keyed on intent rather than on the inbox path: the widget is
                // embedded by several URL shapes (/{app}, /{app}/inbox after the
                // not-found rewrite, /app?appCode=…), and only an explicit
                // compose request should survive as compose.
                //
                // An open Settings overlay is left alone. `force-ui-view=SETTINGS`
                // is what ui-store writes back into a query-param URL whenever
                // Settings is opened, so reloading the widget from Settings
                // arrives here with the overlay up — and switching to CHAT would
                // close it, losing the card the user was looking at. Excluded
                // only off the /settings route, where master closes it too and
                // this branch has no business improving on that.
                // Neither `isComposeRoute` nor a MESSAGE_FORM force-ui-view is
                // consulted, because for a chat-only app neither is a request
                // the user made. Both are written by the app itself:
                // closeSettings re-syncs the path from the underlying view, so
                // /inbox becomes /compose behind the user's back; and on a
                // query-param URL — the widget's own shape — ui-store stamps
                // force-ui-view=<view> on EVERY setView, so the first load's
                // provisional MESSAGE_FORM is echoed back into the URL and read
                // on the next load as an explicit compose. Trusting either made
                // the handoff work once and never again.
                //
                // Trade: someone who clicks Compose and then reloads while on a
                // record gets the chat back. Accepted — for this app the record's
                // chat is the point, and Compose is one click away in the nav.
                const chatOnlyAwaitingRecord =
                  CHAT_ONLY_TPAS.has(currentTpaCode) &&
                  currentView === 'MESSAGE_FORM';
                if (tpaSupportsInbox && (currentView === 'CONVERSATIONS' || chatOnlyAwaitingRecord) && forceView !== 'CONVERSATIONS') {
                  const entityIds = wc.entityId.split(',').filter(Boolean);
                  const sdkView = entityIds.length === 1 ? 'CHAT' : 'MESSAGE_FORM';
                  // Chat-only apps only. Settings is open over the top, so apply
                  // the view underneath it rather than through setView, which
                  // would close the overlay and lose the card the user is on.
                  // Bare setState also leaves the path alone, which matters:
                  // closeSettings re-syncs the URL from activeView, so writing
                  // /compose here would resolve the deliberate
                  // (MESSAGE_FORM, /inbox) pair above into a /compose the next
                  // load can no longer read as an inbox open.
                  //
                  // The CHAT_ONLY test is load-bearing, not decoration: the outer
                  // condition also admits any inbox app sitting on CONVERSATIONS,
                  // and for those master closes the overlay here. Without this
                  // conjunct twilio, messagebird, whatsapp, twiliocall, intercom
                  // and ringcentral would all keep an overlay master dismisses.
                  if (
                    CHAT_ONLY_TPAS.has(currentTpaCode) &&
                    useUIStore.getState().settingsOpen &&
                    !isSettingsRoute
                  ) {
                    useUIStore.setState({ activeView: sdkView });
                    logWithTimestamp(`View updated to ${sdkView} beneath the open Settings overlay (step 4c)`);
                  } else {
                    useUIStore.getState().setView(sdkView);
                    logWithTimestamp(`View updated to ${sdkView} from SDK widget context (step 4c)`);
                  }
                }
              }
            };

            // For modules that resolve widget context asynchronously (e.g.
            // Zendesk's ZAF handshake), don't block init/auth — apply the view
            // switch once the context resolves in the background. Modules that
            // resolve synchronously in onInit apply it immediately.
            if (saasModule.ensureWidgetContext) {
              saasModule.ensureWidgetContext().then(applyWidgetContextView);
            } else {
              applyWidgetContextView();
            }

            // Trigger initial resize now that SaaS module is loaded
            // (step 3 set the view before module was available, so resizeForView was a no-op)
            const currentView = useUIStore.getState().activeView;
            saasModule.resizeForView?.(currentView);
          }
        }

        // 5. Start SaaS auth check (non-blocking — resolved in BG after READY).
        //    Runs concurrently with settings + teams to save time.
        logWithTimestamp('START: SaaS auth check (non-blocking)');
        const { checkSaaSAuth } = useModuleStore.getState();
        const saasAuthPromise = checkSaaSAuth();

        // 6. Ensure orgId and load settings + team permissions
        logWithTimestamp('START: Ensuring orgId and settings');
        setCurrentStep('org');

        // 6a. If no cached orgId (first-time user), we must await SaaS auth to fetch it.
        //     With cached orgId (returning user), SaaS auth resolves in BG after READY.
        if (!context.config.orgId) {
          logWithTimestamp('No cached orgId — awaiting SaaS auth to fetch org');
          const saasAuthed = await saasAuthPromise;
          logWithTimestamp(`SaaS auth resolved for orgId (authenticated: ${saasAuthed})`);
          const saasModule = useModuleStore.getState().saasModule;
          if (saasAuthed && saasModule) {
            // Authorized — resolve orgId (needed for settings/Firestore reads).
            try {
              const crmUser = useModuleStore.getState().saasUser;
              if (crmUser?.id) {
                context.config.appUniqueId = crmUser.id;
                writeScopedCache('ua_cache_appUniqueId', crmUser.id, cacheScope);
              }
              const org = await saasModule.getCurrentOrg().catch(() => null);
              if (org?.id) {
                liveOrgId = org.id;
                writeScopedCache('ua_cache_appOrgId', org.id, cacheScope);
                context.config.orgId = org.id;
              }
            } catch {
              logWithTimestamp('ERROR: Failed to fetch org (non-critical)');
            }
            if (context.config.orgId) {
              useAppStore.getState().setConfig(context.config);
              logWithTimestamp(`appConfig set with orgId: ${context.config.orgId}`);
            }
          } else {
            // NOT authorized — the "Authorize" prompt should appear as soon as
            // possible. Don't block `ready` on:
            //   • getCurrentOrg() — there's no authorized org to fetch, and for
            //     some SaaS (e.g. Zendesk) it re-issues a failing getCurrentUser.
            //   • the suggested-login-email lookup — for Zendesk that's a ZAF
            //     handshake/round-trip. Resolve it in the background and let the
            //     prompt populate it reactively when it arrives.
            suggestedEmailPromise
              .then((suggestedEmail) => {
                if (suggestedEmail) {
                  logWithTimestamp(`Suggested login email (bg): ${suggestedEmail}`);
                  useAuthStore.getState().setSuggestedLoginEmail(suggestedEmail);
                }
              })
              .catch(() => { /* non-critical */ });
          }
        }

        // 6d. Settings + Team permissions — loaded in parallel to reduce time.
        const hasCachedSettings = !!getStorageItem('app_preferences', null);

        if (hasCachedSettings) {
          // Cache is available — mark settingsLoaded so components gated on it can proceed.
          // Firestore sync in BG Tier 1 will update if anything changed remotely.
          useSettingsStore.setState({ settingsLoaded: true, savedTemplatesLoaded: true });

          // Restore trial status from localStorage cache (normally set by loadAllSettings)
          try {
            const cachedTrialStart = readScopedCache('ua_cache_trial_start', cacheScope);
            useTrialStore.getState().setTrialFromSettings(
              cachedTrialStart ? parseInt(cachedTrialStart, 10) : null
            );
          } catch { /* */ }

          // Restore shared admin user ID from localStorage
          const ext = useAppStore.getState().extensionName;
          if (ext && user.uid && !useAuthStore.getState().selectedAdminUserId) {
            try {
              const cachedAdminId = localStorage.getItem(
                `UA_LIC_UTILITY___CURRENT_SELECTED_ORG_ADMIN_USER_ID_${ext}_${user.uid}`
              );
              if (cachedAdminId) {
                useAuthStore.getState().setSelectedAdminSilent(cachedAdminId, null);
                logWithTimestamp(`Restored selectedAdminUserId from localStorage: ${cachedAdminId}`);
              }
            } catch { /* */ }
          }

          // Restore team permissions from localStorage cache (fetched fresh in BG via loadAllSettings)
          import('@/stores/team-store').then(({ useTeamStore }) => {
            useTeamStore.getState().restoreFromCache();
          }).catch(() => {});

          logWithTimestamp('DONE: Settings served from localStorage cache (Firestore sync in background)');
        }

        // 6d. Settings: block only if no localStorage cache (first-time user)
        if (!hasCachedSettings) {
          logWithTimestamp('START: Settings blocking load (no cache)');
          try {
            await useSettingsStore.getState().loadAllSettings();
            logWithTimestamp('DONE: Settings loaded from Firestore');
          } catch (e) {
            console.warn('[AppInit] Settings load failed:', e);
          }
        }

        // Safety net: ensure settingsLoaded is true even if loadAllSettings() couldn't
        // read from Firestore (e.g. missing orgId when SaaS isn't authorized yet).
        if (!useSettingsStore.getState().settingsLoaded) {
          useSettingsStore.setState({ settingsLoaded: true });
          logWithTimestamp('Safety net: settingsLoaded forced to true');
        }
        // 6b. Ensure Twilio Event Streams sink exists (non-blocking, fire-and-forget).
        //     Must wait for a fresh Firestore settings load before checking, so we
        //     read the actual `ua_twilio_sink_id` and don't trigger the CF based on
        //     stale localStorage cache. If settings were already loaded blocking
        //     above (no cache path), this Promise resolves immediately.
        {
          const resolvedTPA = useAppStore.getState().appCodeTPA || appCodeTPA || '';
          const tpaModuleForSink = useModuleStore.getState().tpaModule;
          const tpaAuthedForSink = useModuleStore.getState().tpaAuthenticated;
          if (resolvedTPA === 'twilio' && tpaAuthedForSink && tpaModuleForSink && 'ensureEventSink' in tpaModuleForSink) {
            const runSink = async () => {
              if (hasCachedSettings) {
                // Settings came from cache — sync from Firestore first to get real sink_id
                try {
                  await useSettingsStore.getState().loadAllSettings();
                } catch (e) {
                  console.warn('[AppInit] Settings sync before sink check failed:', e);
                }
              }
              await (tpaModuleForSink as { ensureEventSink: () => Promise<void> }).ensureEventSink();
            };
            runSink().catch((err: unknown) => {
              console.warn('[AppInit] Twilio event sink setup failed:', err);
            });
          }
        }

        // 6c. Zoho CRM org-mismatch check (non-blocking, fire-and-forget).
        //     Users sometimes authorize Ulgebra in one Zoho org and then
        //     open the extension inside another — every API read/write
        //     would then hit the wrong org. Compare the authorized org
        //     (OAuth token) against the iframe org (embedded-app SDK) and
        //     surface a full-page overlay via useUIStore.orgMismatch if
        //     they differ. The overlay's CTA re-runs OAuth.
        {
          const resolvedSAAS = useAppStore.getState().appCodeSAAS || appCodeSAAS || '';
          const saasModuleForOrg = useModuleStore.getState().saasModule;
          const saasAuthedForOrg = useModuleStore.getState().saasAuthenticated;
          if (resolvedSAAS === 'zohocrm' && saasAuthedForOrg && saasModuleForOrg && 'getIframeOrgId' in saasModuleForOrg) {
            (async () => {
              try {
                const iframeOrgId = await (saasModuleForOrg as { getIframeOrgId: () => Promise<string | null> }).getIframeOrgId();
                if (!iframeOrgId) return; // SDK unavailable — can't check, don't flag
                const authorizedOrg = await saasModuleForOrg.getCurrentOrg();
                if (!authorizedOrg?.id) return;
                if (String(authorizedOrg.id) !== String(iframeOrgId)) {
                  useUIStore.getState().setOrgMismatch({
                    saasId: 'zohocrm',
                    authorizedOrgId: String(authorizedOrg.id),
                    authorizedOrgName: authorizedOrg.name,
                    currentOrgId: String(iframeOrgId),
                  });
                  logWithTimestamp(`Zoho org mismatch: authorized=${authorizedOrg.id} iframe=${iframeOrgId}`);
                }
              } catch (err) {
                console.warn('[AppInit] Zoho org-mismatch check failed:', err);
              }
            })();
          }
        }

        // 7. CRM records — deferred to immediate BG task after READY.
        //    saasAuth runs concurrently with settings+teams and resolves in BG.
        //    crmRecordsLoading (set in step 2) gates conversations-view until records arrive.
        setCurrentStep('finalizing');

        // Clear crmRecordsLoading for non-CRM contexts so conversations-view can proceed.
        // CRM contexts keep it true — the BG task below will clear it after records load.
        if (useModuleStore.getState().crmRecordsLoading && !useUIStore.getState().crmRecordContext) {
          useModuleStore.getState().setCrmRecordsLoading(false);
          logWithTimestamp('Cleared crmRecordsLoading (no CRM context detected)');
        }

        // 8. View is already resolved early, CRM context may have updated it
        // No need to set viewResolved again here

        // 9. Read onboarding nstep from preferences and set store accordingly
        //    nstep: undefined/<4 → show timeline (steps 1-3) + auto-redirect to incoming sync
        //    nstep 4 → show country code dialog
        //    nstep 5 → show notification dialog
        //    nstep 6+ → onboarding complete, nothing to show
        {
          const currentUid = useAuthStore.getState().user?.uid || '';
          const orgId = useAppStore.getState().appConfig?.orgId;
          const isUlgebraAdmin = (await import('@/config/constants')).ADMIN_UIDS.has(currentUid);
          if (isUlgebraAdmin && orgId) {
            const prefs = useSettingsStore.getState().preferences as Record<string, unknown>;
            // Read nstep from preferences, with localStorage fallback
            // (localStorage is updated immediately; Firestore may lag if orgId wasn't ready)
            const prefsNstep = typeof prefs?.nstep === 'number' ? prefs.nstep : 0;
            const lsNstep = parseInt(readScopedCache('ua_onboarding_nstep', cacheScope) || '0', 10) || 0;
            const nstep = Math.max(prefsNstep, lsNstep);
            console.log('[Init] Onboarding nstep:', nstep, '(prefs:', prefsNstep, 'ls:', lsNstep, ')');

            const { useOnboardingStore } = await import('@/stores/onboarding-store');
            useOnboardingStore.getState().setNstep(nstep);

            if (nstep < 4) {
              // Steps 1-3: show timeline bar, app handles auth normally, step 3 auto-redirects
              useOnboardingStore.getState().setOnboardingRequired(true);
              if (useAuthStore.getState().selectedAdminUserId) {
                useOnboardingStore.getState().setIsSharedUser(true);
              }
              logWithTimestamp(`Onboarding timeline required (nstep=${nstep})`);
            } else if (nstep === 4) {
              // Country-code prompt is disabled — advance straight to the
              // notification step. Non-messaging/event apps still skip to 6.
              const tpaCode = useAppStore.getState().appCodeTPA ?? '';
              const isEventApp = (EVENT_APPS as readonly string[]).includes(tpaCode);
              const isNonMessaging = NON_MESSAGING_TPAS.has(tpaCode);
              if (isEventApp || isNonMessaging) {
                useOnboardingStore.getState().updateNstep(6);
                logWithTimestamp('Onboarding steps 4-5 skipped (non-messaging app)');
              } else {
                useOnboardingStore.getState().updateNstep(5);
                useOnboardingStore.getState().setPendingPromptStep('notification');
                logWithTimestamp('Onboarding step 4 bypassed → notification dialog');
              }
            } else if (nstep === 5) {
              // Step 5: show notification full-page dialog
              useOnboardingStore.getState().setPendingPromptStep('notification');
              logWithTimestamp('Onboarding step 5: notification dialog');
            }
            // nstep >= 6: nothing to show
          }
        }

        // 10. Mark as READY immediately — the app shell can render now
        setCurrentStep('done');
        setReady(true);
        initCompletedRef.current = true;

        // Notify Freshdesk parent frame that the app is initialized and allowed
        if (useAppStore.getState().appCodeSAAS === 'freshdesk' || useAppStore.getState().appCodeSAAS === 'freshworkscrm') {
          try {
            window.parent.postMessage({ eventType: 'setConfigValue', key: 'isAllow', value: true }, '*');
          } catch { /* not embedded */ }
        }

        // 10a. Immediate background: SaaS auth + org refresh + CRM records + suggested email.
        //      Fires immediately after READY (no delay) — CRM context users need records ASAP.
        //      saasAuthPromise was started in step 5 and ran concurrently with settings+teams,
        //      so it's likely already resolved by now.
        (async () => {
          try {
            const saasAuthed = await saasAuthPromise;
            logWithTimestamp(`BG-immediate: SaaS auth resolved (authenticated: ${saasAuthed})`);

            // Suggested email — only needed when SaaS not authed
            if (!saasAuthed) {
              const suggestedEmail = await suggestedEmailPromise;
              if (suggestedEmail) {
                logWithTimestamp(`Suggested login email: ${suggestedEmail}`);
                useAuthStore.getState().setSuggestedLoginEmail(suggestedEmail);
              }
            }

            // Org fetch + appConfig refresh (returns fresh orgId, appUniqueId)
            if (saasAuthed) {
              const saasModule = useModuleStore.getState().saasModule;
              if (saasModule) {
                try {
                  const crmUser = useModuleStore.getState().saasUser;
                  const org = await saasModule.getCurrentOrg().catch(() => null);
                  if (crmUser?.id) {
                    writeScopedCache('ua_cache_appUniqueId', crmUser.id, cacheScope);
                  }
                  if (org?.id) {
                    liveOrgId = org.id;
                    writeScopedCache('ua_cache_appOrgId', org.id, cacheScope);
                    const currentConfig = useAppStore.getState().appConfig;
                    if (currentConfig) {
                      const needsUpdate = currentConfig.orgId !== org.id
                        || (crmUser?.id && currentConfig.appUniqueId !== crmUser.id);
                      if (needsUpdate) {
                        useAppStore.getState().setConfig({
                          ...currentConfig,
                          orgId: org.id,
                          appUniqueId: crmUser?.id || currentConfig.appUniqueId,
                        });
                        logWithTimestamp(`BG-immediate: appConfig updated (orgId: ${org.id})`);
                      }
                    }
                  }
                } catch {
                  logWithTimestamp('BG-immediate: Failed to fetch org (non-critical)');
                }
              }

              // Ensure SaaS-side incoming webhooks exist for modules that
              // declare them (ISaaSModule.ensureIncomingWebhooks — e.g.
              // Freshdesk's desk-reply automation rule). Duck-typed and
              // fire-and-forget, mirroring the TPA-side ensureEventSink
              // pattern in 6b. Modules check their own settings internally.
              // Messaging apps only — the legacy `isUASMSApp` gate: event,
              // call, file and invoice apps have no desk-reply flow.
              (async () => {
                try {
                  if (!saasModule.ensureIncomingWebhooks) return;
                  const tpaCode = useAppStore.getState().appCodeTPA || '';
                  const isMessagingTPA = !(EVENT_APPS as readonly string[]).includes(tpaCode)
                    && !(CALL_APPS as readonly string[]).includes(tpaCode)
                    && !NON_MESSAGING_TPAS.has(tpaCode);
                  if (!isMessagingTPA) return;
                  if (!useModuleStore.getState().tpaAuthenticated) return;
                  const extName = useAppStore.getState().extensionName;
                  if (!extName) return;
                  let token = useModuleStore.getState().webhookToken;
                  if (!token?.saas) {
                    await useModuleStore.getState().fetchWebhookToken(extName);
                    token = useModuleStore.getState().webhookToken;
                  }
                  if (!token?.saas) return;
                  await saasModule.ensureIncomingWebhooks(token.saas);
                  logWithTimestamp('BG-immediate: SaaS incoming webhooks ensured');
                } catch (err) {
                  console.warn('[AppInit] SaaS incoming webhook ensure failed:', err);
                }
              })();
            }

            // Ensure async widget-context resolution (e.g. Zendesk's ZAF
            // handshake) has finished before the CRM-record gate. onInit no
            // longer blocks on it, so without this the gate could read a stale
            // (false) crmRecordContext and skip the record fetch. Its
            // resolution (step 4c) is what sets crmRecordContext.
            if (saasAuthed) {
              await useModuleStore.getState().saasModule?.ensureWidgetContext?.();
            }

            // CRM records — full fetch with IDB cache + fresh network
            if (saasAuthed && useUIStore.getState().crmRecordContext) {
              const saasModule = useModuleStore.getState().saasModule;
              if (saasModule) {
                try {
                  const wc = saasModule.getWidgetContext?.();
                  if (!wc?.module || !wc?.entityId) {
                    // No widget context to load from — settle the phase
                    // immediately so chat-view's "Loading record…"
                    // placeholder doesn't hang.
                    useModuleStore.getState().setCrmRecordsLoadPhase('done');
                  }
                  if (wc?.module && wc?.entityId) {
                    console.log('[AppInit] BG-immediate: Loading CRM record context:', wc);
                    useModuleStore.getState().setCrmRecordsLoading(true);
                    // Phase: looking up cached CRM context in IDB. Usually
                    // very brief (<50ms) but useful to surface so the
                    // chat-view placeholder doesn't read "Select a sender…"
                    // during the IDB read.
                    useModuleStore.getState().setCrmRecordsLoadPhase('cache-lookup');

                    const ext = useAppStore.getState().extensionName || '';
                    const crmCacheKey = getCrmContextCacheKey(ext, wc.module, wc.entityId);

                    // Helper: apply CRM context data to stores
                    const applyCrmContext = (records: CRMRecord[], phoneFields: CrmPhoneField[], moduleFields: CRMField[]) => {
                      useModuleStore.getState().setCrmRecords(records);
                      if (phoneFields.length > 0) {
                        useUIStore.getState().setCrmPhoneFields(phoneFields);
                        useUIStore.getState().setCrmSelectedPhoneField(phoneFields[0].key || phoneFields[0].apiName);
                      }
                      if (moduleFields.length > 0) {
                        useUIStore.getState().setCrmModuleFields(moduleFields);
                      }
                      const tpaModule = useModuleStore.getState().tpaModule;
                      const recipients = saasModule.addRecordsAsRecipients(records, tpaModule ?? undefined);
                      if (recipients.length > 0) {
                        useMessageStore.getState().addRecipients(recipients);
                      }
                      useUIStore.getState().setCrmRecordContext(true);
                    };

                    // Helper: extract phone fields from records + module field definitions
                    const extractPhoneFields = (records: CRMRecord[], moduleFields: CRMField[]): CrmPhoneField[] => {
                      const PHONE_KEYWORDS = ['phone', 'mobile', 'fax', 'tel'];
                      const phoneFieldDefs = moduleFields.filter((f) => {
                        const isPhoneType = f.dataType === 'phone';
                        const labelMatch = PHONE_KEYWORDS.some((kw) =>
                          f.displayLabel.toLowerCase().includes(kw)
                        );
                        return isPhoneType || labelMatch;
                      });

                      const isMultiRecord = records.length > 1;
                      const seenValues = new Set<string>();
                      const phoneFields: CrmPhoneField[] = [];
                      const schemaFieldNames = new Set(phoneFieldDefs.map((f) => f.apiName));

                      for (const record of records) {
                        for (const fd of phoneFieldDefs) {
                          let rawVal = record.data?.[fd.apiName];
                          if (rawVal === undefined || rawVal === null) {
                            const dotIdx = fd.apiName.indexOf('.');
                            const nestedPath = dotIdx >= 0 ? fd.apiName.substring(dotIdx + 1) : fd.apiName;
                            rawVal = nestedPath.split('.').reduce<unknown>(
                              (obj, key) => (obj && typeof obj === 'object' ? (obj as Record<string, unknown>)[key] : undefined),
                              record.data
                            );
                          }
                          const rawStr = String(rawVal || '').trim();
                          if (!rawStr) continue;
                          const vals = rawStr.includes(',') ? rawStr.split(',').map((v) => v.trim()).filter(Boolean) : [rawStr];
                          for (let vi = 0; vi < vals.length; vi++) {
                            const val = vals[vi];
                            const normalized = val.replace(/\D/g, '');
                            if (seenValues.has(normalized)) continue;
                            seenValues.add(normalized);
                            const suffix = vals.length > 1 ? ` ${vi + 1}` : '';
                            phoneFields.push({
                              apiName: fd.apiName,
                              displayLabel: isMultiRecord
                                ? `${record.displayName || record.id} — ${fd.displayLabel}${suffix}`
                                : `${fd.displayLabel}${suffix}`,
                              value: val,
                              key: isMultiRecord ? `${record.id}_${fd.apiName}_${vi}` : `${fd.apiName}_${vi}`,
                              recordName: isMultiRecord ? (record.displayName || record.id) : undefined,
                            });
                          }
                        }
                        if (record.data) {
                          for (const key of Object.keys(record.data)) {
                            if (!key.startsWith('phone_') || schemaFieldNames.has(key)) continue;
                            const rawStr = String(record.data[key] || '').trim();
                            if (!rawStr) continue;
                            const vals = rawStr.includes(',') ? rawStr.split(',').map((v) => v.trim()).filter(Boolean) : [rawStr];
                            for (let vi = 0; vi < vals.length; vi++) {
                              const val = vals[vi];
                              const normalized = val.replace(/\D/g, '');
                              if (seenValues.has(normalized)) continue;
                              seenValues.add(normalized);
                              const label = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                              const suffix = vals.length > 1 ? ` ${vi + 1}` : '';
                              phoneFields.push({
                                apiName: key,
                                displayLabel: isMultiRecord
                                  ? `${record.displayName || record.id} — ${label}${suffix}`
                                  : `${label}${suffix}`,
                                value: val,
                                key: isMultiRecord ? `${record.id}_${key}_${vi}` : `${key}_${vi}`,
                                recordName: isMultiRecord ? (record.displayName || record.id) : undefined,
                              });
                            }
                          }
                        }
                      }
                      // WhatsApp username user (no phone): fall back to the user ID in the dummy email
                      if (phoneFields.length === 0 && useModuleStore.getState().tpaModule?.id === 'whatsapp') {
                        return extractWaUsernameFields(records);
                      }
                      return phoneFields;
                    };

                    // ── Check IDB cache first — apply instantly if available ──
                    const cachedCtx = await idbCache.get<CachedCrmContext>('org_data', crmCacheKey);
                    if (cachedCtx && cachedCtx.phones?.length > 0) {
                      logWithTimestamp(`IDB HIT → record "${cachedCtx.displayName}", ${cachedCtx.phones.length} phones`);

                      const cachedData: Record<string, unknown> = {};
                      if (cachedCtx.properties) {
                        for (const [k, v] of Object.entries(cachedCtx.properties)) {
                          cachedData[k] = v;
                        }
                      }
                      for (const ph of cachedCtx.phones) {
                        cachedData[ph.fieldName] = ph.value;
                      }
                      const cachedRecord: CRMRecord = {
                        id: cachedCtx.id,
                        module: wc.module,
                        displayName: cachedCtx.displayName,
                        email: cachedCtx.email,
                        phone: cachedCtx.phones[0]?.value,
                        data: cachedData,
                      };

                      const cachedPhoneFields: CrmPhoneField[] = cachedCtx.phones.map(ph => ({
                        apiName: ph.fieldName,
                        // Prefer the persisted label (carries the augmented
                        // "(Module)" suffix); fall back to the raw field name
                        // for caches written before labels were stored.
                        displayLabel: ph.label || ph.fieldName,
                        value: ph.value,
                        key: ph.fieldName,
                      }));

                      applyCrmContext([cachedRecord], cachedPhoneFields, []);
                      useModuleStore.getState().setCrmRecordsLoading(false);
                      // Phase: cache hit applied, but we still hit the
                      // SaaS API in the background to refresh stale
                      // phones / module fields. Empty states defer
                      // rendering "Select a sender…" until phase clears,
                      // since phone fields can still mutate below and a
                      // conversation may auto-construct.
                      useModuleStore.getState().setCrmRecordsLoadPhase('cache-hit-bg-refresh');
                      logWithTimestamp('CRM context applied from IDB cache (instant)');

                      // Background refresh — fetch fresh records + update stores
                      Promise.all([
                        saasModule.fetchRecords(wc.module, wc.entityId),
                        saasModule.getModuleFields(wc.module).catch(() => [] as CRMField[]),
                      ]).then(([freshRecords, freshModuleFields]) => {
                        if (freshRecords.length > 0) {
                          const freshPhoneFields = extractPhoneFields(freshRecords, freshModuleFields);
                          logWithTimestamp(`Background CRM refresh → ${freshRecords.length} records, ${freshPhoneFields.length} phones`);

                          useModuleStore.getState().setCrmRecords(freshRecords);
                          if (freshPhoneFields.length > 0) {
                            const currentPhones = useUIStore.getState().crmPhoneFields;
                            const phonesChanged = freshPhoneFields.length !== currentPhones.length
                              || freshPhoneFields.some((fp, i) => fp.value !== currentPhones[i]?.value || fp.apiName !== currentPhones[i]?.apiName || fp.displayLabel !== currentPhones[i]?.displayLabel);
                            if (phonesChanged) {
                              useUIStore.getState().setCrmPhoneFields(freshPhoneFields);
                              logWithTimestamp('Background CRM refresh: phone fields changed, updating store');
                            }
                          }
                          if (freshModuleFields.length > 0) {
                            useUIStore.getState().setCrmModuleFields(freshModuleFields);
                          }

                          // ── Re-derive CRM recipients from fresh records ──
                          // The cache-hit path's first applyCrmContext call
                          // produced recipients whose `properties` map only
                          // includes the limited string/number props we
                          // persist to IDB (nested-object lookups like
                          // Owner / Account_Name are intentionally excluded
                          // from the cache to keep its size bounded). The
                          // chat-input's # merge-field dropdown sources its
                          // keys from `activeCrmRecipient.properties`, so
                          // without this re-derive the dropdown is stuck
                          // showing the cached subset even though the
                          // module store now has the full fresh record.
                          //
                          // Replace CRM recipients in place — preserves any
                          // manual/csv recipients the user may have added
                          // alongside the CRM context.
                          const tpaModuleNow = useModuleStore.getState().tpaModule;
                          const freshRecipients = saasModule.addRecordsAsRecipients(freshRecords, tpaModuleNow ?? undefined);
                          if (freshRecipients.length > 0) {
                            const messageStore = useMessageStore.getState();
                            const nonCrmRecipients = messageStore.recipients.filter((r) => r.source !== 'crm');
                            messageStore.setRecipients([...nonCrmRecipients, ...freshRecipients]);
                          }

                          if (freshRecords.length === 1) {
                            const rec = freshRecords[0];
                            const bgProps: Record<string, string> = {};
                            if (rec.data) {
                              for (const [k, v] of Object.entries(rec.data)) {
                                if (v != null && typeof v !== 'object') bgProps[k] = String(v);
                              }
                            }
                            if (rec.displayName) bgProps['name'] = rec.displayName;
                            if (rec.email) bgProps['email'] = rec.email;
                            const cacheData: CachedCrmContext = {
                              id: rec.id,
                              displayName: rec.displayName,
                              email: rec.email,
                              phones: freshPhoneFields.map(pf => ({ fieldName: pf.apiName, value: pf.value, label: pf.displayLabel })),
                              properties: bgProps,
                            };
                            idbCache.set('org_data', crmCacheKey, cacheData, CRM_CONTEXT_CACHE_TTL).catch(() => {});
                          }
                        }
                      }).catch((err) => {
                        console.warn('[AppInit] Background CRM refresh failed:', err);
                      }).finally(() => {
                        useModuleStore.getState().setCrmRecordsLoadPhase('done');
                      });
                    } else {
                      // ── No cache — fetch fresh with streaming progress ──
                      // Phase: pulling records from SaaS API directly.
                      // The chat-view placeholder reads this as "Fetching
                      // record from API…" until records arrive.
                      useModuleStore.getState().setCrmRecordsLoadPhase('fetching-fresh');
                      const entityIds = wc.entityId.split(',').map((id: string) => id.trim()).filter(Boolean);
                      const moduleFieldsPromise = saasModule.getModuleFields(wc.module).catch(() => [] as CRMField[]);

                      if (entityIds.length > 1) {
                        useModuleStore.getState().setCrmRecordsProgress(entityIds.length, 0);
                        useUIStore.getState().setCrmRecordContext(true);

                        const moduleFields = await moduleFieldsPromise;
                        if (moduleFields.length > 0) {
                          useUIStore.getState().setCrmModuleFields(moduleFields);
                        }

                        const allRecords: CRMRecord[] = [];
                        const tpaModule = useModuleStore.getState().tpaModule;

                        // Per-record processing — extracted so both the
                        // batched and per-id paths emit the same UI
                        // updates (append record, phone fields, recipients).
                        const processRecord = (rec: CRMRecord) => {
                          allRecords.push(rec);
                          useModuleStore.getState().appendCrmRecord(rec);

                          const phoneFields = extractPhoneFields([rec], moduleFields);
                          if (phoneFields.length > 0) {
                            const currentPhones = useUIStore.getState().crmPhoneFields;
                            useUIStore.getState().setCrmPhoneFields([...currentPhones, ...phoneFields]);
                            if (!useUIStore.getState().crmSelectedPhoneField) {
                              useUIStore.getState().setCrmSelectedPhoneField(phoneFields[0].key || phoneFields[0].apiName);
                            }
                          }
                          const recipients = saasModule.addRecordsAsRecipients([rec], tpaModule ?? undefined);
                          if (recipients.length > 0) {
                            useMessageStore.getState().addRecipients(recipients);
                          }
                        };

                        // Prefer the batched multi-id fetch when the SaaS
                        // module exposes one (Zoho CRM does — `?ids=` in
                        // one shot). This collapses N→1 roundtrip and
                        // sidesteps the per-user concurrent-connection
                        // cap that throttles the per-id loop below
                        // (Zoho ~5, HubSpot ~10 → ~10-20% of parallel
                        // calls return empty data, rejected as "No
                        // records found").
                        if (typeof saasModule.fetchRecordsByIds === 'function') {
                          try {
                            const batched = await saasModule.fetchRecordsByIds(wc.module, entityIds);
                            for (const rec of batched) processRecord(rec);
                          } catch (err) {
                            console.warn('[AppInit] Batched fetchRecordsByIds failed, falling back to per-id loop:', err);
                            // Fallback to the per-id loop below if batched call throws.
                            await runWithConcurrency(entityIds, 4, async (eid: string) => {
                              try {
                                const recs = await saasModule.fetchRecords(wc.module, eid);
                                for (const rec of recs) processRecord(rec);
                              } catch (e) {
                                console.warn(`[AppInit] Failed to fetch CRM record ${eid}:`, e);
                              }
                            });
                          }
                        } else {
                          // Modules without a batch endpoint (HubSpot,
                          // Pipedrive, Salesforce, etc.) — cap concurrency
                          // at 4 to stay under upstream connection limits
                          // while still pipelining. Local rate guard is
                          // already bypassed for bulk mode; this guards
                          // against the *upstream's* limit.
                          await runWithConcurrency(entityIds, 4, async (eid: string) => {
                            try {
                              const recs = await saasModule.fetchRecords(wc.module, eid);
                              for (const rec of recs) processRecord(rec);
                            } catch (err) {
                              console.warn(`[AppInit] Failed to fetch CRM record ${eid}:`, err);
                            }
                          });
                        }

                        useModuleStore.getState().setCrmRecordsLoading(false);
                        useModuleStore.getState().setCrmRecordsLoadPhase('done');
                        logWithTimestamp(`CRM streaming complete: ${allRecords.length} records`);
                      } else {
                        const [records, moduleFields] = await Promise.all([
                          saasModule.fetchRecords(wc.module, wc.entityId),
                          moduleFieldsPromise,
                        ]);

                        console.log('[AppInit] Fetched CRM records:', records.length);
                        const phoneFields = extractPhoneFields(records, moduleFields);
                        console.log('[AppInit] CRM phone fields:', phoneFields.length, phoneFields.map((f) => `${f.key}=${f.value}`));

                        applyCrmContext(records, phoneFields, moduleFields);
                        useModuleStore.getState().setCrmRecordsLoading(false);
                        useModuleStore.getState().setCrmRecordsLoadPhase('done');

                        if (records.length === 1) {
                          const rec = records[0];
                          const props: Record<string, string> = {};
                          if (rec.data) {
                            for (const [k, v] of Object.entries(rec.data)) {
                              if (v != null && typeof v !== 'object') {
                                props[k] = String(v);
                              }
                            }
                          }
                          if (rec.displayName) props['name'] = rec.displayName;
                          if (rec.email) props['email'] = rec.email;
                          const cacheData: CachedCrmContext = {
                            id: rec.id,
                            displayName: rec.displayName,
                            email: rec.email,
                            phones: phoneFields.map(pf => ({ fieldName: pf.apiName, value: pf.value, label: pf.displayLabel })),
                            properties: props,
                          };
                          idbCache.set('org_data', crmCacheKey, cacheData, CRM_CONTEXT_CACHE_TTL).catch(() => {});
                          logWithTimestamp(`CRM context cached to IDB (${Object.keys(props).length} properties)`);
                        }
                      }
                    }
                  }
                } catch (err) {
                  console.warn('[AppInit] Failed to load CRM record context:', err);
                  useModuleStore.getState().setCrmRecordsLoading(false);
                  useModuleStore.getState().setCrmRecordsLoadPhase('done');
                }
              }
            }

            // Fallback: clear crmRecordsLoading if still set
            if (useModuleStore.getState().crmRecordsLoading) {
              useModuleStore.getState().setCrmRecordsLoading(false);
              useModuleStore.getState().setCrmRecordsLoadPhase('done');
              logWithTimestamp('BG-immediate: Cleared crmRecordsLoading (fallback)');
            }
          } catch (err) {
            console.error('[AppInit] BG-immediate task failed:', err);
            if (useModuleStore.getState().crmRecordsLoading) {
              useModuleStore.getState().setCrmRecordsLoading(false);
              useModuleStore.getState().setCrmRecordsLoadPhase('done');
            }
          }
        })();

        // 10b. Background tasks: settings sync, credits, org users.
        //      Delayed until at least 4s after page load so the critical-path
        //      network calls (conversations, senders, messages) finish first.
        const msSincePageLoad = performance.now() - PAGE_LOAD_TIME;
        const bgDelay = Math.max(0, 4_000 - msSincePageLoad);
        logWithTimestamp(`Background tasks scheduled in ${bgDelay.toFixed(0)}ms`);

        setTimeout(() => {
          logWithTimestamp('START: Background tasks (settings sync, credits, org users)');

          // Auto-save default history module settings for Zoho Recruit
          // Ported from old app: CALL_AFTER_PERSONAL_COMMON_SETTING_RESOLVED → setIncomingHistory
          if (useModuleStore.getState().saasAuthenticated) {
            const bgSaasModule = useModuleStore.getState().saasModule;
            if (bgSaasModule && 'initHistoryModuleSettings' in bgSaasModule) {
              (bgSaasModule as unknown as { initHistoryModuleSettings: () => Promise<void> })
                .initHistoryModuleSettings()
                .catch((e) => console.warn('[AppInit] History module init failed:', e));
            }
          }

          Promise.all([
            (async () => {
              if (hasCachedSettings) {
                // Cache was used in step 6d — now sync from Firestore in background
                try {
                  await useSettingsStore.getState().loadAllSettings();
                  logWithTimestamp('DONE: Settings synced from Firestore (background)');
                } catch (e) {
                  console.warn('[AppInit] Background settings sync failed:', e);
                }
              }
              // Trial: initialize for first-time users if settings-store didn't find a trial start time.
              // This is a no-op if trial was already set during loadAllSettings().
              try {
                const { useTrialStore } = await import('@/stores/trial-store');
                await useTrialStore.getState().initializeTrialIfNeeded();
              } catch (e) {
                console.warn('[AppInit] Trial initialization failed:', e);
              }
              // org_id: keep the extension doc pointing at the org we're
              // actually connected to. Paired with the trial init above
              // exactly as in the legacy app — the trial half was ported,
              // this half wasn't, so the dashboard kept resolving a stale
              // org and read a different trial doc than the app wrote.
              try {
                const { syncExtensionOrgId } = await import('@/lib/crm-sync');
                // Deliberately NOT appConfig.orgId: that's cache-seeded at
                // startup and only overwritten once the org API succeeds, so
                // writing it after a failed call would persist a stale org —
                // the very drift this sync repairs.
                //
                // `liveOrgId` alone isn't enough either. It's set by the
                // cold-start path (skipped entirely when a cached orgId
                // exists) and by the un-awaited BG-immediate block, while
                // this runs on a ~4s timer — so a warm-cache session, or a
                // getCurrentOrg() slower than the timer, would leave it null
                // and the sync would silently never happen. Resolve it here
                // when that's the case.
                let orgId = liveOrgId;
                if (!orgId && useModuleStore.getState().saasAuthenticated) {
                  const mod = useModuleStore.getState().saasModule;
                  orgId = (await mod?.getCurrentOrg().catch(() => null))?.id ?? null;
                }
                const ext = useAppStore.getState().extensionName;
                const updated = await syncExtensionOrgId(user.uid, ext, orgId);
                if (updated) {
                  console.log('[AppInit] Synced extension org_id →', updated);
                } else if (!orgId) {
                  // Log the skip too — a silent no-op here is why a stale
                  // org_id can survive indefinitely.
                  logWithTimestamp('org_id sync skipped — no live org resolved');
                }
              } catch (e) {
                console.warn('[AppInit] org_id sync failed:', e);
              }
            })(),
            useAppUsersStore.getState().loadOrgUsers().catch((e) => {
              console.warn('[AppInit] Background org users load failed:', e);
            }),
            // Shared users: confirm the admin they're operating as still lists
            // them. A removal only touches the admin's Firestore docs, so
            // without this the stale selection in localStorage / personal
            // settings / token claims survives every reload. Reads the invite
            // list, which the header has usually already loaded — the call is
            // a no-op then. Revoking reloads the page.
            useAuthStore.getState().verifySelectedAdminMembership().catch((e) => {
              console.warn('[AppInit] Shared-admin membership check failed:', e);
            }),
            // Sync ulgebraUsers/{uid} doc (isAdmin, inAppCodes, preferredCurrency)
            // and write back CRM fields kept fresh by the legacy
            // UA_ULGEBRA_CRM_UTILITY (inAppCodes, timezoneText). Deferred so it
            // doesn't fire on page load — cached values are used until now.
            (async () => {
              try {
                const { syncAuthorizedAppCode, syncTimezone } = await import('@/lib/crm-sync');
                const uid = user.uid;
                // Same read the login check started (no second Firestore read).
                const userData = await loadUserDocOnce(uid);
                console.log('[CRM] ulgebraUsers doc:', userData);
                if (!userData) return;

                // Legacy-parity phone prompt: if ph is empty (never collected,
                // and not the 100001 "user declined" sentinel), show the
                // notification dialog. Skipped during any active onboarding
                // step so we don't stack dialogs.
                if (!userData.ph) {
                  const { useOnboardingStore } = await import('@/stores/onboarding-store');
                  const onb = useOnboardingStore.getState();
                  if (!onb.pendingPromptStep && !onb.onboardingRequired) {
                    onb.setPendingPromptStep('notification');
                  }
                }
                const current = useAuthStore.getState().user;
                if (current?.uid === uid) {
                  const updated = { ...current };
                  let changed = false;
                  if (userData.isAdmin && !updated.isAdmin) { updated.isAdmin = true; changed = true; }
                  if (userData.inAppCodes && JSON.stringify(userData.inAppCodes) !== JSON.stringify(updated.inAppCodes)) { updated.inAppCodes = userData.inAppCodes; changed = true; }
                  if (userData.preferredCurrency && userData.preferredCurrency !== updated.preferredCurrency) { updated.preferredCurrency = userData.preferredCurrency; changed = true; }
                  // Both ways, so support can also turn the fixed-IP flag off.
                  if (!!userData.useZohoFixedIp !== !!updated.useZohoFixedIp) { updated.useZohoFixedIp = !!userData.useZohoFixedIp; changed = true; }
                  if (changed) {
                    useAuthStore.setState({ user: updated });
                    try { localStorage.setItem('ua_cache_user', JSON.stringify(updated)); } catch {}
                    logWithTimestamp('DONE: User doc synced from Firestore (background)');
                  }
                }

                const extensionName = useAppStore.getState().extensionName;
                const nextCodes = await syncAuthorizedAppCode(uid, extensionName, userData.inAppCodes);
                if (nextCodes) {
                  const c = useAuthStore.getState().user;
                  if (c?.uid === uid) {
                    const u = { ...c, inAppCodes: nextCodes };
                    useAuthStore.setState({ user: u });
                    try { localStorage.setItem('ua_cache_user', JSON.stringify(u)); } catch {}
                  }
                }
                await syncTimezone(uid, userData.timezoneText);
              } catch (e) {
                console.warn('[AppInit] Background user doc sync failed:', e);
              }
            })(),
          ]);
        }, bgDelay);

        // L2 background sync: license data — lower priority, 10s after page load.
        const l2Delay = Math.max(0, 10_000 - (performance.now() - PAGE_LOAD_TIME));
        setTimeout(() => {
          logWithTimestamp('START: L2 background tasks (license sync, incoming sync check)');

          // RTDB alert path drainage: server-side bulk delete of stale
          // entries (>3min) under
          // /instantUserConversationMessageAlerts/{ext|extforpage}/{uid}/.
          // Fire-and-forget; throttled to once per page-load by the helper.
          // Latched here (not at listener attach) because by +10s auth is
          // guaranteed ready, and the L2 sync is already the right home
          // for low-priority maintenance tasks.
          const extensionName = useAppStore.getState().extensionName;
          if (extensionName) triggerAlertsCleanup(extensionName);

          Promise.all([
            useLicenseStore.getState().fetchLicenseSubscriptions().catch((e) => {
              console.warn('[AppInit] L2 license subscriptions fetch failed:', e);
            }),
            useLicenseStore.getState().fetchCredits(getEffectiveUid() || user.uid).catch((e) => {
              console.warn('[AppInit] L2 credits fetch failed:', e);
            }),
            // Background incoming sync health check
            (async () => {
              try {
                const tpaModule = useModuleStore.getState().tpaModule;
                const tpaAuthenticated = useModuleStore.getState().tpaAuthenticated;
                if (!tpaModule || !tpaAuthenticated) return;

                // Check if user explicitly dismissed the alert
                const prefs = useSettingsStore.getState().preferences;
                if (prefs.hideIncomingSyncAlert) return;

                const tpaCode = useAppStore.getState().appCodeTPA ?? '';

                // Twilio: skip the banner entirely. Twilio users manage incoming
                // sync through Event Streams sinks + messaging services, not via
                // the generic webhook check — the alert would false-positive.
                if (tpaCode === 'twilio') return;

                let incomingConfigured = false;

                if (tpaCode === 'whatsapp') {
                  // WhatsApp: app subscribed AND at least one phone registered
                  if ('getWABAStatuses' in tpaModule) {
                    const statuses = await (tpaModule as any).getWABAStatuses();
                    incomingConfigured = Array.isArray(statuses) && statuses.some(
                      (w: any) => w.subscribedApps?.length > 0 && w.phoneNumbers?.length > 0
                    );
                  }
                } else if (tpaModule.checkWebhookExists) {
                  // All other TPAs (MessageBird, RingCentral, Calendly, Acuity, etc.)
                  incomingConfigured = await tpaModule.checkWebhookExists();
                } else {
                  // No check method available — assume OK
                  incomingConfigured = true;
                }

                logWithTimestamp(`Incoming sync check: ${tpaCode} configured=${incomingConfigured}`);
                if (!incomingConfigured) {
                  useUIStore.getState().setIncomingSyncAlert(true);
                }
              } catch (err) {
                console.warn('[AppInit] Background incoming sync check failed:', err);
              }
            })(),
          ]);
        }, l2Delay);
      } catch (error) {
        setInitError(error instanceof Error ? error.message : 'Initialization failed');
        setReady(true);
        initCompletedRef.current = true;
      }
    };

    init();

    // Cleanup function — DO NOT reset initStartedRef
    // In StrictMode, React unmounts/remounts but refs persist
    // We want init to run only ONCE even across remounts
    return () => {
      console.log('[AppInit] Cleanup called, initCompleted:', initCompletedRef.current);
      // No-op - refs intentionally persist
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return {
    ready,
    initError,
    currentStep,
    stepLabel: STEP_LABELS[currentStep],
    appCodeTPA,
    appCodeSAAS,
    extensionName,
    modulesLoading: saasLoading || tpaLoading,
    moduleErrors: [saasError, tpaError].filter(Boolean) as string[],
  };
}
